// Workforce Calendar — admin-only scheduling module
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, Edit, Trash2, Check, X,
  Calendar as CalendarIcon, Download,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { flagFor } from "@/lib/countries";
import { DatePicker } from "@/components/ui/date-picker";

// ---------------- Helpers ----------------
const PROJECT_COLORS = {
  "DAL 1998":   "#3b2bd0",
  "DAL 3581":   "#0ea5e9",
  "DAL 3734":   "#14b8a6",
  "EIR 251":    "#f59e0b",
  "EIR 5367":   "#ef4444",
  "DAL Admin":  "#8b5cf6",
  "DAL 4888":   "#22c55e",
  "CDT General":"#ec4899",
};
const fallbackPalette = ["#3b2bd0","#0ea5e9","#14b8a6","#f59e0b","#ef4444","#8b5cf6","#22c55e","#ec4899","#64748b","#a16207"];
function colorFor(name) {
  if (PROJECT_COLORS[name]) return PROJECT_COLORS[name];
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return fallbackPalette[Math.abs(h) % fallbackPalette.length];
}
const STATUS_STYLES = {
  scheduled: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  completed: "bg-[#100755] text-[#b9b2e8] border-[#3b2bd0]/40",
  cancelled: "bg-red-500/10 text-red-300 border-red-500/30 line-through",
};

function startOfWeek(d) {
  const x = new Date(d); const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmt(d) { return d.toISOString().slice(0, 10); }
function monthLabel(d) { return d.toLocaleDateString(undefined, { month: "long", year: "numeric" }); }
function shortDay(d) { return d.toLocaleDateString(undefined, { weekday: "short" }); }

// Get the calendar grid for a month (always 6 weeks starting Monday)
function monthGrid(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

const VIEW_TABS = [
  { id: "month",    label: "Month" },
  { id: "week",     label: "Week" },
  { id: "day",      label: "Day" },
  { id: "employee", label: "By Employee" },
  { id: "project",  label: "By Project" },
];

const EMPTY = {
  employee_id: "", project_id: "",
  date: new Date().toISOString().slice(0, 10),
  start_time: "08:00", end_time: "16:00",
  location: "", role: "", notes: "", status: "scheduled",
  // Multi-day fields (UI only)
  multi_day: false,
  end_date: new Date().toISOString().slice(0, 10),
  weekdays: [0, 1, 2, 3, 4], // Mon-Fri
};

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ---------------- Page ----------------
export default function Calendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [anchor, setAnchor] = useState(new Date());
  const [view, setView] = useState("month");
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterEmp, setFilterEmp] = useState("all");
  const [filterProj, setFilterProj] = useState("all");
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // Details preview state — shown when an assignment/leave pill is clicked
  const [detail, setDetail] = useState(null); // { type: 'assignment'|'leave', data: {...} }

  // Compute date range based on view
  const range = useMemo(() => {
    if (view === "month") {
      const grid = monthGrid(anchor);
      return { start: fmt(grid[0]), end: fmt(grid[grid.length - 1]) };
    }
    if (view === "week" || view === "employee" || view === "project") {
      const ws = startOfWeek(anchor);
      return { start: fmt(ws), end: fmt(addDays(ws, 6)) };
    }
    // day
    return { start: fmt(anchor), end: fmt(anchor) };
  }, [view, anchor]);

  const [leaves, setLeaves] = useState([]);

  const reload = async () => {
    const params = { start_date: range.start, end_date: range.end };
    if (filterEmp !== "all") params.employee_id = filterEmp;
    if (filterProj !== "all") params.project_id = filterProj;
    const leaveParams = {};
    if (filterEmp !== "all") leaveParams.user_id = filterEmp;
    const [aRes, eRes, pRes, lRes] = await Promise.all([
      api.get("/assignments", { params }),
      api.get("/users"),
      api.get("/projects"),
      api.get("/leaves", { params: leaveParams }),
    ]);
    setAssignments(aRes.data);
    setEmployees(eRes.data.filter((u) => u.role === "employee" && u.active));
    setProjects(pRes.data);
    // Only show approved + pending leaves on the calendar
    setLeaves(lRes.data.filter((l) => l.status === "approved" || l.status === "pending"));
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, filterEmp, filterProj]);

  const openCreate = (presetDate) => {
    setEditing(null);
    setForm({
      ...EMPTY,
      date: presetDate || fmt(new Date()),
      end_date: presetDate || fmt(new Date()),
      employee_id: isAdmin ? "" : user.id,
    });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      ...EMPTY,
      employee_id: a.employee_id, project_id: a.project_id, date: a.date,
      start_time: a.start_time, end_time: a.end_time,
      location: a.location || "", role: a.role || "", notes: a.notes || "",
      status: a.status,
      end_date: a.date,
    });
    setOpen(true);
  };

  // Show preview card on pill click (works for both assignments and leaves)
  const openDetails = (assignment) => setDetail({ type: "assignment", data: assignment });
  const openLeaveDetails = (leave) => setDetail({ type: "leave", data: leave });

  const decideLeaveFromDialog = async (leaveId, status) => {
    try {
      await api.post(`/leaves/${leaveId}/decision`, { status });
      toast.success(`Leave ${status}`);
      setDetail(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteLeaveFromDialog = async (leaveId) => {
    if (!window.confirm("Delete this leave request? This cannot be undone.")) return;
    try {
      await api.delete(`/leaves/${leaveId}`);
      toast.success("Leave deleted");
      setDetail(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.project_id) return toast.error("Project is required");
    const targetEmp = isAdmin ? form.employee_id : user.id;
    if (!targetEmp) return toast.error("Employee is required");
    try {
      if (editing) {
        await api.patch(`/assignments/${editing}`, {
          employee_id: targetEmp,
          project_id: form.project_id,
          date: form.date,
          start_time: form.start_time, end_time: form.end_time,
          location: form.location, role: form.role, notes: form.notes,
          status: form.status,
        });
        toast.success("Assignment updated");
      } else if (form.multi_day) {
        if (!form.weekdays.length) return toast.error("Pick at least one weekday");
        const res = await api.post("/assignments/bulk", {
          employee_id: targetEmp,
          project_id: form.project_id,
          start_date: form.date, end_date: form.end_date,
          start_time: form.start_time, end_time: form.end_time,
          weekdays: form.weekdays,
          location: form.location, role: form.role, notes: form.notes,
          status: form.status,
        });
        toast.success(`${res.data.created} assignment${res.data.created === 1 ? "" : "s"} created`);
      } else {
        await api.post("/assignments", {
          employee_id: targetEmp,
          project_id: form.project_id,
          date: form.date,
          start_time: form.start_time, end_time: form.end_time,
          location: form.location, role: form.role, notes: form.notes,
          status: form.status,
        });
        toast.success("Assignment created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this assignment?")) return;
    try { await api.delete(`/assignments/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Navigation
  const move = (dir) => {
    const x = new Date(anchor);
    if (view === "month") x.setMonth(x.getMonth() + dir);
    else if (view === "day") x.setDate(x.getDate() + dir);
    else x.setDate(x.getDate() + dir * 7);
    setAnchor(x);
  };
  const today = () => setAnchor(new Date());

  // Title
  const periodLabel = useMemo(() => {
    if (view === "month") return monthLabel(anchor);
    if (view === "day") return anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const ws = startOfWeek(anchor);
    return `Week of ${ws.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
  }, [view, anchor]);

  // Group assignments by date string
  const byDate = useMemo(() => {
    const m = {};
    assignments.forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    return m;
  }, [assignments]);

  // Expand leaves into per-date entries, restricted to the visible range
  const leavesByDate = useMemo(() => {
    const m = {};
    const rs = new Date(range.start);
    const re = new Date(range.end);
    leaves.forEach((l) => {
      // If a project filter is active, skip leaves entirely (leaves aren't tied to projects)
      if (filterProj !== "all") return;
      const ls = new Date(l.start_date);
      const le = new Date(l.end_date);
      const from = ls > rs ? ls : rs;
      const to = le < re ? le : re;
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const k = d.toISOString().slice(0, 10);
        (m[k] = m[k] || []).push(l);
      }
    });
    return m;
  }, [leaves, range.start, range.end, filterProj]);

  // Export
  const exportFile = async (fmt_) => {
    const params = new URLSearchParams({ fmt: fmt_, period: "custom", start_date: range.start, end_date: range.end });
    if (filterEmp !== "all") params.append("employee_id", filterEmp);
    if (filterProj !== "all") params.append("project_id", filterProj);
    const r = await api.get(`/exports/calendar?${params.toString()}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const ext = fmt_ === "xlsx" ? "xlsx" : fmt_ === "pdf" ? "pdf" : "csv";
    const a = document.createElement("a"); a.href = url; a.download = `calendar.${ext}`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <div className="label-eyebrow">WORKFORCE CALENDAR</div>
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight mt-1">Schedule</h1>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="hidden sm:flex gap-2">
              <Button variant="ghost" onClick={() => exportFile("csv")} className="rounded-none border border-[#222] font-mono text-xs"><Download className="h-3 w-3 mr-1" /> CSV</Button>
              <Button variant="ghost" onClick={() => exportFile("xlsx")} className="rounded-none border border-[#222] font-mono text-xs"><Download className="h-3 w-3 mr-1" /> XLSX</Button>
              <Button variant="ghost" onClick={() => exportFile("pdf")} className="rounded-none border border-[#222] font-mono text-xs"><Download className="h-3 w-3 mr-1" /> PDF</Button>
            </div>
            <Select onValueChange={(v) => exportFile(v)}>
              <SelectTrigger className="sm:hidden h-10 w-[110px] bg-black border-[#222] rounded-none font-mono text-xs" data-testid="export-mobile">
                <Download className="h-3 w-3 mr-1" /><SelectValue placeholder="EXPORT" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">XLSX</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => openCreate()} data-testid="new-assignment-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase text-xs sm:text-sm">
              <Plus className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">New Assignment</span><span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        {/* View tabs — horizontally scrollable on mobile */}
        <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
          <div className="flex gap-1 border border-[#1c1c1c] p-1 bg-[#0a0a0a] w-fit">
            {VIEW_TABS.map((v) => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                data-testid={`view-${v.id}`}
                className={`px-3 sm:px-4 py-2 text-[11px] sm:text-xs font-heading uppercase tracking-wider whitespace-nowrap ${view === v.id ? "bg-[#100755] text-white" : "text-[#888] hover:text-white"}`}
              >{v.label}</button>
            ))}
          </div>
        </div>

        {/* Filters + Nav */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:flex-wrap">
            {isAdmin && (
              <div className="w-full sm:min-w-[180px] sm:w-auto">
                <Label className="label-eyebrow">EMPLOYEE</Label>
                <Select value={filterEmp} onValueChange={setFilterEmp}>
                  <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono w-full"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                    <SelectItem value="all">All employees</SelectItem>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-full sm:min-w-[180px] sm:w-auto">
              <Label className="label-eyebrow">PROJECT</Label>
              <Select value={filterProj} onValueChange={setFilterProj}>
                <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono w-full"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                  <SelectItem value="all">All projects</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => move(-1)} className="rounded-none border border-[#222] h-10 px-3"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" onClick={today} className="rounded-none border border-[#222] h-10 font-mono text-xs uppercase px-3">Today</Button>
            <Button variant="ghost" onClick={() => move(1)} className="rounded-none border border-[#222] h-10 px-3"><ChevronRight className="h-4 w-4" /></Button>
            <div className="ml-1 sm:ml-3 font-heading text-sm sm:text-lg uppercase tracking-tight">{periodLabel}</div>
          </div>
        </div>
      </div>

      {/* The actual view */}
      {view === "month" && (
        <MonthView anchor={anchor} byDate={byDate} leavesByDate={leavesByDate} openCreate={openCreate} openDetails={openDetails} openLeaveDetails={openLeaveDetails} employees={employees} />
      )}
      {view === "week" && (
        <WeekView anchor={anchor} byDate={byDate} leavesByDate={leavesByDate} openCreate={openCreate} openDetails={openDetails} openLeaveDetails={openLeaveDetails} employees={employees} />
      )}
      {view === "day" && (
        <DayView anchor={anchor} byDate={byDate} leavesByDate={leavesByDate} openCreate={openCreate} openDetails={openDetails} openLeaveDetails={openLeaveDetails} employees={employees} />
      )}
      {view === "employee" && (
        <EmployeeView anchor={anchor} assignments={assignments} leaves={leaves} employees={employees} openDetails={openDetails} openLeaveDetails={openLeaveDetails} />
      )}
      {view === "project" && (
        <ProjectView anchor={anchor} assignments={assignments} projects={projects} openDetails={openDetails} />
      )}

      {/* Project color legend */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-4">
        <div className="label-eyebrow mb-3">PROJECT COLOUR KEY</div>
        <div className="flex flex-wrap gap-3 text-xs font-mono">
          {projects.map((p) => (
            <span key={p.id} className="flex items-center gap-2">
              <span className="h-3 w-3 inline-block" style={{ background: colorFor(p.name) }} />
              {p.name}
            </span>
          ))}
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 inline-block bg-amber-500/40 border border-amber-500/70" />
            Leave (approved)
          </span>
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 inline-block bg-amber-500/10 border border-dashed border-amber-500/50" />
            Leave (pending)
          </span>
        </div>
      </Card>

      {/* Details preview dialog (assignment or leave) */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none text-white max-w-md" data-testid="details-dialog">
          {detail?.type === "assignment" && (
            <AssignmentDetails
              a={detail.data}
              isAdmin={isAdmin}
              isOwner={user?.id === detail.data.employee_id}
              onEdit={() => { setDetail(null); openEdit(detail.data); }}
              onDelete={() => { setDetail(null); remove(detail.data.id); }}
            />
          )}
          {detail?.type === "leave" && (
            <LeaveDetails
              l={detail.data}
              isAdmin={isAdmin}
              isOwner={user?.id === detail.data.user_id}
              onApprove={() => decideLeaveFromDialog(detail.data.id, "approved")}
              onReject={() => decideLeaveFromDialog(detail.data.id, "rejected")}
              onDelete={() => deleteLeaveFromDialog(detail.data.id)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none text-white max-w-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">{editing ? "Edit Assignment" : "New Assignment"}</DialogTitle>
            <DialogDescription className="text-xs font-mono text-[#888]">
              Assign an employee to a project for a specific date and time window. Conflicting time slots will be highlighted.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isAdmin && (
              <div>
                <Label className="label-eyebrow">EMPLOYEE</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="assign-employee" className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="label-eyebrow">PROJECT</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="assign-project" className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 inline-block" style={{ background: colorFor(p.name) }} />
                        {p.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">{form.multi_day && !editing ? "FROM DATE" : "DATE"}</Label>
              <div className="mt-2"><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="assign-date" /></div>
            </div>
            {form.multi_day && !editing && (
              <div>
                <Label className="label-eyebrow">END DATE</Label>
                <div className="mt-2">
                  <DatePicker
                    value={form.end_date}
                    onChange={(v) => setForm({ ...form, end_date: v })}
                    testid="assign-end-date"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="label-eyebrow">STATUS</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="label-eyebrow">START</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" /></div>
            <div><Label className="label-eyebrow">END</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" /></div>
            <div><Label className="label-eyebrow">LOCATION</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" /></div>
            <div><Label className="label-eyebrow">ROLE / TASK</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" /></div>
            <div className="sm:col-span-2"><Label className="label-eyebrow">NOTES</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-black border-[#222] rounded-none mt-2 font-mono text-sm" /></div>

            {/* Multi-day block — only for new assignments */}
            {!editing && (
              <div className="sm:col-span-2 border border-[#1c1c1c] p-3">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.multi_day}
                    onChange={(e) => setForm({ ...form, multi_day: e.target.checked })}
                    className="accent-white"
                    data-testid="multi-day-toggle"
                  />
                  Multi-day assignment (e.g. Monday → Friday)
                </label>
                {form.multi_day && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <Label className="label-eyebrow">END DATE</Label>
                      <div className="mt-2">
                        <DatePicker
                          value={form.end_date}
                          onChange={(v) => setForm({ ...form, end_date: v })}
                          testid="assign-end-date"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="label-eyebrow">DAYS OF WEEK</Label>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {WEEKDAY_LABELS.map((lbl, idx) => {
                          const on = form.weekdays.includes(idx);
                          return (
                            <button
                              key={lbl}
                              type="button"
                              onClick={() => setForm({
                                ...form,
                                weekdays: on
                                  ? form.weekdays.filter((x) => x !== idx)
                                  : [...form.weekdays, idx].sort(),
                              })}
                              data-testid={`weekday-${lbl.toLowerCase()}`}
                              className={`px-3 py-1.5 text-xs font-heading uppercase tracking-wider border ${on ? "bg-[#100755] border-[#3b2bd0] text-white" : "border-[#222] text-[#888] hover:text-white"}`}
                            >{lbl}</button>
                          );
                        })}
                      </div>
                      <div className="font-mono text-[10px] text-[#666] mt-2">
                        Will create one assignment for each selected weekday between the dates.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {editing && (
              <Button variant="ghost" onClick={() => { remove(editing); setOpen(false); }} className="rounded-none border border-red-500/30 text-red-400 font-heading uppercase">
                <Trash2 className="h-3 w-3 mr-2" /> Delete
              </Button>
            )}
            <Button onClick={save} data-testid="save-assignment-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Sub-views ----------------
// Status badge style helper
function statusBadge(s) {
  return STATUS_STYLES[s] || STATUS_STYLES.scheduled;
}

// Details card shown when clicking an assignment pill
function AssignmentDetails({ a, isAdmin, isOwner, onEdit, onDelete }) {
  const bg = colorFor(a.project_name);
  const canEdit = isAdmin || isOwner;
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading uppercase flex items-center gap-2">
          <span className="h-3 w-3 inline-block" style={{ background: bg }} />
          Assignment
        </DialogTitle>
        <DialogDescription className="text-xs font-mono text-[#888]">
          {a.date} · {a.start_time}–{a.end_time}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 text-sm" data-testid="assignment-details">
        <DetailRow k="EMPLOYEE">{a.employee_name}</DetailRow>
        <DetailRow k="PROJECT">
          <span className="inline-flex items-center gap-2">
            <span className="h-2 w-2 inline-block" style={{ background: bg }} />
            {a.project_name}
          </span>
        </DetailRow>
        <DetailRow k="STATUS">
          <Badge variant="outline" className={`rounded-none font-mono text-[10px] uppercase ${statusBadge(a.status)}`}>{a.status}</Badge>
        </DetailRow>
        {a.location && <DetailRow k="LOCATION">{a.location}</DetailRow>}
        {a.role && <DetailRow k="ROLE / TASK">{a.role}</DetailRow>}
        {a.notes && <DetailRow k="NOTES"><span className="whitespace-pre-wrap text-[#ddd]">{a.notes}</span></DetailRow>}
      </div>
      <DialogFooter className="gap-2 mt-4">
        {canEdit && (
          <Button
            variant="ghost"
            onClick={onDelete}
            data-testid="details-delete-btn"
            className="rounded-none border border-red-500/40 text-red-400 hover:bg-red-500/10 font-heading uppercase"
          >
            <Trash2 className="h-3 w-3 mr-2" /> Delete
          </Button>
        )}
        {canEdit && (
          <Button
            onClick={onEdit}
            data-testid="details-edit-btn"
            className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase"
          >
            <Edit className="h-3 w-3 mr-2" /> Edit
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

// Details card shown when clicking a leave pill
function LeaveDetails({ l, isAdmin, isOwner, onApprove, onReject, onDelete }) {
  const statusCls = l.status === "approved" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    : l.status === "rejected" ? "bg-red-500/10 text-red-400 border-red-500/30"
    : "bg-amber-500/10 text-amber-400 border-amber-500/30";
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading uppercase flex items-center gap-2">
          <span className="h-3 w-3 inline-block bg-amber-500/70" />
          Leave
        </DialogTitle>
        <DialogDescription className="text-xs font-mono text-[#888]">
          {l.start_date} → {l.end_date} · {l.days || "—"} day(s)
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 text-sm" data-testid="leave-details">
        <DetailRow k="EMPLOYEE">{l.user_name}</DetailRow>
        <DetailRow k="TYPE"><span className="label-eyebrow">{(l.leave_type || "").toUpperCase()}</span></DetailRow>
        <DetailRow k="STATUS">
          <Badge variant="outline" className={`rounded-none font-mono text-[10px] uppercase ${statusCls}`}>{l.status}</Badge>
        </DetailRow>
        {l.reason && <DetailRow k="REASON"><span className="whitespace-pre-wrap text-[#ddd]">{l.reason}</span></DetailRow>}
        {l.decision_note && <DetailRow k="DECISION NOTE"><span className="whitespace-pre-wrap text-[#ddd]">{l.decision_note}</span></DetailRow>}
      </div>
      <DialogFooter className="gap-2 mt-4 flex-wrap">
        {isAdmin && l.status === "pending" && (
          <>
            <Button
              variant="ghost"
              onClick={onReject}
              data-testid="leave-reject-btn"
              className="rounded-none border border-red-500/40 text-red-400 hover:bg-red-500/10 font-heading uppercase"
            >
              <X className="h-3 w-3 mr-2" /> Reject
            </Button>
            <Button
              onClick={onApprove}
              data-testid="leave-approve-btn"
              className="rounded-none bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30 font-heading uppercase"
            >
              <Check className="h-3 w-3 mr-2" /> Approve
            </Button>
          </>
        )}
        {(isAdmin || (isOwner && l.status === "pending")) && (
          <Button
            variant="ghost"
            onClick={onDelete}
            data-testid="leave-delete-btn"
            className="rounded-none border border-red-500/40 text-red-400 hover:bg-red-500/10 font-heading uppercase"
          >
            <Trash2 className="h-3 w-3 mr-2" /> Delete
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

function DetailRow({ k, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
      <div className="label-eyebrow pt-0.5">{k}</div>
      <div className="text-[#eee]">{children}</div>
    </div>
  );
}

function Pill({ a, empMap, onClick }) {
  const bg = colorFor(a.project_name);
  const emp = empMap?.[a.employee_id];
  const flag = emp ? flagFor(emp.country) : "";
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(a); }}
      title={`${a.employee_name} · ${a.project_name} · ${a.start_time}-${a.end_time}`}
      className={`w-full text-left text-[10px] font-mono uppercase tracking-wide px-1.5 py-1 truncate border ${a.status === "cancelled" ? "line-through opacity-60" : ""}`}
      style={{
        backgroundColor: `${bg}33`,
        borderColor: `${bg}AA`,
        color: "#fff",
      }}
    >
      <span className="opacity-90">{a.start_time}</span> {flag && <span className="mr-0.5">{flag}</span>}{a.employee_name.split(" ")[0]} · {a.project_name}
    </button>
  );
}

// Visual styles per leave status — approved is solid amber, pending is dashed
function LeavePill({ l, empMap, onClick }) {
  const emp = empMap?.[l.user_id];
  const flag = emp ? flagFor(emp.country) : "";
  const approved = l.status === "approved";
  const cls = approved
    ? "bg-amber-500/20 border-amber-500/70 text-amber-100"
    : "bg-amber-500/5 border-dashed border-amber-500/50 text-amber-200/70";
  const typeLabel = (l.leave_type || "leave").toUpperCase();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick && onClick(l); }}
      title={`${l.user_name} · ${typeLabel} ${l.start_date}→${l.end_date}${l.status === "pending" ? " (pending)" : ""}`}
      className={`w-full text-left text-[10px] font-mono uppercase tracking-wide px-1.5 py-1 truncate border ${cls} hover:brightness-125`}
      data-testid="leave-pill"
    >
      {flag && <span className="mr-0.5">{flag}</span>}
      {(l.user_name || "").split(" ")[0]} · {typeLabel}{!approved && " ?"}
    </button>
  );
}

function MonthView({ anchor, byDate, leavesByDate, employees, openCreate, openDetails, openLeaveDetails }) {
  const empMap = useMemo(() => Object.fromEntries(employees.map((u) => [u.id, u])), [employees]);
  const grid = monthGrid(anchor);
  const currentMonth = anchor.getMonth();
  const today = fmt(new Date());
  return (
    <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-hidden" data-testid="month-view">
      <div className="grid grid-cols-7 border-b border-[#1c1c1c] bg-brand-soft">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div key={i} className="px-1 sm:px-3 py-1.5 sm:py-2 text-center sm:text-left text-[10px] sm:text-[11px] font-heading uppercase tracking-wider text-[#8a8a8a] border-r border-[#1c1c1c] last:border-r-0">
            <span className="sm:hidden">{d}</span>
            <span className="hidden sm:inline">{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(64px, auto)" }}>
        {grid.map((d, i) => {
          const k = fmt(d);
          const inMonth = d.getMonth() === currentMonth;
          const isToday = k === today;
          const list = byDate[k] || [];
          const leaveList = (leavesByDate && leavesByDate[k]) || [];
          const total = list.length + leaveList.length;
          return (
            <div
              key={i}
              onClick={() => openCreate(k)}
              className={`border-r border-b border-[#1c1c1c] p-1 sm:p-2 cursor-pointer hover:bg-[#101010] sm:min-h-[120px] ${inMonth ? "" : "bg-black/40 opacity-50"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] sm:text-xs font-mono tabular ${isToday ? "bg-[#100755] text-white px-1 sm:px-1.5 py-0.5" : "text-[#888]"}`}>
                  {d.getDate()}
                </span>
                {total > 0 && <span className="text-[9px] sm:text-[10px] text-[#666] font-mono">{total}</span>}
              </div>
              {/* Compact dots on small screens, full pills on sm+ */}
              <div className="space-y-1 hidden sm:block">
                {leaveList.slice(0, 2).map((l) => <LeavePill key={`l-${l.id}`} l={l} empMap={empMap} onClick={openLeaveDetails} />)}
                {list.slice(0, Math.max(0, 4 - Math.min(2, leaveList.length))).map((a) => <Pill key={a.id} a={a} empMap={empMap} onClick={openDetails} />)}
                {total > 4 && <div className="text-[10px] font-mono text-[#888] px-1">+{total - 4} more</div>}
              </div>
              <div className="flex flex-wrap gap-0.5 sm:hidden">
                {leaveList.slice(0, 6).map((l) => (
                  <span key={`l-${l.id}`} className="h-1.5 w-1.5 inline-block bg-amber-500/80" title={`${l.user_name} · ${(l.leave_type||"").toUpperCase()}`} />
                ))}
                {list.slice(0, 6).map((a) => (
                  <span key={a.id} className="h-1.5 w-1.5 inline-block" style={{ backgroundColor: colorFor(a.project_name) }} title={`${a.employee_name} · ${a.project_name}`} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function WeekView({ anchor, byDate, leavesByDate, employees, openCreate, openDetails, openLeaveDetails }) {
  const empMap = useMemo(() => Object.fromEntries(employees.map((u) => [u.id, u])), [employees]);
  const ws = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const today = fmt(new Date());
  return (
    <>
      {/* Desktop / tablet: 7-column grid */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-hidden hidden md:block" data-testid="week-view">
        <div className="grid grid-cols-7 border-b border-[#1c1c1c] bg-brand-soft">
          {days.map((d) => {
            const k = fmt(d);
            return (
              <div key={k} className="px-3 py-2 border-r border-[#1c1c1c] last:border-r-0">
                <div className={`label-eyebrow ${k === today ? "text-white" : ""}`}>{shortDay(d)}</div>
                <div className={`font-mono text-xs tabular ${k === today ? "text-white" : "text-[#888]"}`}>{d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-7 min-h-[420px]">
          {days.map((d) => {
            const k = fmt(d);
            const list = byDate[k] || [];
            const leaveList = (leavesByDate && leavesByDate[k]) || [];
            return (
              <div key={k} onClick={() => openCreate(k)} className="border-r border-[#1c1c1c] last:border-r-0 p-2 space-y-1 hover:bg-[#101010] cursor-pointer">
                {leaveList.map((l) => <LeavePill key={`l-${l.id}`} l={l} empMap={empMap} onClick={openLeaveDetails} />)}
                {list.map((a) => <Pill key={a.id} a={a} empMap={empMap} onClick={openDetails} />)}
                {(list.length + leaveList.length) === 0 && <div className="text-[10px] font-mono text-[#444] mt-2">— empty —</div>}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Mobile: vertical agenda stack */}
      <div className="md:hidden space-y-2" data-testid="week-view-mobile">
        {days.map((d) => {
          const k = fmt(d);
          const list = byDate[k] || [];
          const leaveList = (leavesByDate && leavesByDate[k]) || [];
          const isToday = k === today;
          return (
            <Card
              key={k}
              onClick={() => openCreate(k)}
              className={`rounded-none border ${isToday ? "border-[#3b2bd0] bg-[#100755]/15" : "border-[#1c1c1c] bg-[#0a0a0a]"} p-3 cursor-pointer active:bg-[#101010]`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-base uppercase tracking-tight">{shortDay(d)}</span>
                  <span className="font-mono text-xs text-[#888]">{d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
                  {isToday && <span className="text-[10px] font-mono uppercase text-[#b9b2e8] tracking-widest">Today</span>}
                </div>
                <span className="text-[10px] font-mono text-[#666]">{list.length + leaveList.length || "—"}</span>
              </div>
              <div className="space-y-1">
                {leaveList.map((l) => <LeavePill key={`l-${l.id}`} l={l} empMap={empMap} onClick={openLeaveDetails} />)}
                {list.map((a) => <Pill key={a.id} a={a} empMap={empMap} onClick={openDetails} />)}
                {(list.length + leaveList.length) === 0 && <div className="text-[10px] font-mono text-[#444] py-1">— no assignments —</div>}
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
}

function DayView({ anchor, byDate, leavesByDate, employees, openCreate, openDetails, openLeaveDetails }) {
  const empMap = useMemo(() => Object.fromEntries(employees.map((u) => [u.id, u])), [employees]);
  const k = fmt(anchor);
  const list = (byDate[k] || []).slice().sort((a, b) => a.start_time.localeCompare(b.start_time));
  const leaveList = (leavesByDate && leavesByDate[k]) || [];
  const HOURS = Array.from({ length: 14 }, (_, i) => i + 6); // 06:00 — 19:00
  return (
    <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-hidden" data-testid="day-view">
      {leaveList.length > 0 && (
        <div className="border-b border-[#1c1c1c] p-3 space-y-1 bg-amber-500/5">
          <div className="label-eyebrow text-amber-300 mb-1">ON LEAVE TODAY</div>
          {leaveList.map((l) => <LeavePill key={`l-${l.id}`} l={l} empMap={empMap} onClick={openLeaveDetails} />)}
        </div>
      )}
      <div className="grid grid-cols-[80px_1fr]">
        <div className="border-r border-[#1c1c1c]">
          {HOURS.map((h) => (
            <div key={h} className="h-14 border-b border-[#1c1c1c] px-2 py-1 text-[10px] font-mono text-[#666]">
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>
        <div className="relative" onClick={() => openCreate(k)}>
          {HOURS.map((h) => <div key={h} className="h-14 border-b border-[#1c1c1c]" />)}
          {list.map((a) => {
            const [sh, sm] = a.start_time.split(":").map(Number);
            const [eh, em] = a.end_time.split(":").map(Number);
            const top = ((sh - 6) * 60 + sm) * (56 / 60);
            const height = Math.max(28, ((eh - sh) * 60 + (em - sm)) * (56 / 60));
            const bg = colorFor(a.project_name);
            const flag = flagFor(empMap[a.employee_id]?.country);
            return (
              <div
                key={a.id}
                onClick={(e) => { e.stopPropagation(); openDetails(a); }}
                className={`absolute left-2 right-2 p-2 border cursor-pointer ${a.status === "cancelled" ? "opacity-50 line-through" : ""}`}
                style={{
                  top: `${top}px`, height: `${height}px`,
                  backgroundColor: `${bg}33`, borderColor: `${bg}DD`,
                }}
                title={`${a.employee_name} · ${a.project_name}`}
              >
                <div className="font-heading text-sm uppercase tracking-tight">{flag} {a.employee_name}</div>
                <div className="font-mono text-[10px] text-white/80">{a.start_time}–{a.end_time} · {a.project_name}</div>
                {a.location && <div className="font-mono text-[10px] text-white/70 mt-0.5">@ {a.location}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function EmployeeView({ anchor, assignments, leaves, employees, openDetails, openLeaveDetails }) {
  const empMap = useMemo(() => Object.fromEntries(employees.map((u) => [u.id, u])), [employees]);
  const ws = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const byEmp = useMemo(() => {
    const m = {};
    assignments.forEach((a) => { (m[a.employee_id] = m[a.employee_id] || []).push(a); });
    return m;
  }, [assignments]);
  // Build per-employee per-date leave lookup
  const leavesByEmpDate = useMemo(() => {
    const m = {};
    (leaves || []).forEach((l) => {
      const ls = new Date(l.start_date);
      const le = new Date(l.end_date);
      for (let d = new Date(ls); d <= le; d.setDate(d.getDate() + 1)) {
        const k = d.toISOString().slice(0, 10);
        const key = `${l.user_id}|${k}`;
        (m[key] = m[key] || []).push(l);
      }
    });
    return m;
  }, [leaves]);
  return (
    <>
      {/* Desktop / tablet table view */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-x-auto hidden md:block" data-testid="employee-view">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-soft border-b border-[#1c1c1c]">
              <th className="px-3 py-2 text-left label-eyebrow w-[220px]">EMPLOYEE</th>
              {days.map((d) => (
                <th key={fmt(d)} className="px-2 py-2 text-left label-eyebrow border-l border-[#1c1c1c]">
                  {shortDay(d)} <span className="text-[#888]">{d.getDate()}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((u) => (
              <tr key={u.id} className="border-t border-[#1c1c1c] hover:bg-[#0d0d0d]">
                <td className="px-3 py-2 font-medium">
                  <span className="flex items-center gap-2">
                    {u.country && <span title={u.country}>{flagFor(u.country)}</span>}
                    {u.name}
                  </span>
                </td>
                {days.map((d) => {
                  const k = fmt(d);
                  const list = (byEmp[u.id] || []).filter((a) => a.date === k);
                  const leaveList = leavesByEmpDate[`${u.id}|${k}`] || [];
                  return (
                    <td key={k} className="px-1 py-1 border-l border-[#1c1c1c] align-top space-y-1 min-w-[140px]">
                      {leaveList.map((l) => <LeavePill key={`l-${l.id}`} l={l} empMap={empMap} onClick={openLeaveDetails} />)}
                      {list.map((a) => <Pill key={a.id} a={a} empMap={empMap} onClick={openDetails} />)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {employees.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center font-mono text-[#666]">No employees</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Mobile: stacked employee cards */}
      <div className="md:hidden space-y-2" data-testid="employee-view-mobile">
        {employees.map((u) => {
          const empAssignments = days.flatMap((d) => {
            const k = fmt(d);
            return (byEmp[u.id] || []).filter((a) => a.date === k).map((a) => ({ ...a, _day: d }));
          });
          const empLeaves = days.flatMap((d) => {
            const k = fmt(d);
            return (leavesByEmpDate[`${u.id}|${k}`] || []).map((l) => ({ ...l, _day: d, _key: `${l.id}|${k}` }));
          });
          const hasItems = empAssignments.length + empLeaves.length > 0;
          return (
            <Card key={u.id} className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-3">
              <div className="flex items-center gap-2 mb-2">
                {u.country && <span title={u.country} className="shrink-0">{flagFor(u.country)}</span>}
                <span className="font-medium truncate">{u.name}</span>
                <span className="ml-auto text-[10px] font-mono text-[#666] shrink-0">{empAssignments.length + empLeaves.length}</span>
              </div>
              {!hasItems && <div className="text-[10px] font-mono text-[#444]">— no items this week —</div>}
              {hasItems && (
                <div className="space-y-1">
                  {empLeaves.map((l) => (
                    <div key={l._key} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase text-[#888] w-10 shrink-0">{shortDay(l._day)}</span>
                      <div className="flex-1 min-w-0"><LeavePill l={l} empMap={empMap} onClick={openLeaveDetails} /></div>
                    </div>
                  ))}
                  {empAssignments.map((a) => (
                    <div key={a.id} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono uppercase text-[#888] w-10 shrink-0">{shortDay(a._day)}</span>
                      <div className="flex-1 min-w-0"><Pill a={a} empMap={empMap} onClick={openDetails} /></div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
        {employees.length === 0 && <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-6 text-center font-mono text-[#666]">No employees</Card>}
      </div>
    </>
  );
}

function ProjectView({ anchor, assignments, projects, openDetails }) {
  const ws = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  const byProj = useMemo(() => {
    const m = {};
    assignments.forEach((a) => { (m[a.project_id] = m[a.project_id] || []).push(a); });
    return m;
  }, [assignments]);
  return (
    <>
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-x-auto hidden md:block" data-testid="project-view">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brand-soft border-b border-[#1c1c1c]">
              <th className="px-3 py-2 text-left label-eyebrow w-[220px]">PROJECT</th>
              {days.map((d) => (
                <th key={fmt(d)} className="px-2 py-2 text-left label-eyebrow border-l border-[#1c1c1c]">
                  {shortDay(d)} <span className="text-[#888]">{d.getDate()}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id} className="border-t border-[#1c1c1c] hover:bg-[#0d0d0d]">
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-3 w-3 inline-block" style={{ background: colorFor(p.name) }} />
                    <span className="font-medium">{p.name}</span>
                  </span>
                </td>
                {days.map((d) => {
                  const k = fmt(d);
                  const list = (byProj[p.id] || []).filter((a) => a.date === k);
                  return (
                    <td key={k} className="px-1 py-1 border-l border-[#1c1c1c] align-top space-y-1 min-w-[140px]">
                      {list.map((a) => (
                        <button
                          key={a.id}
                          onClick={(e) => { e.stopPropagation(); openDetails(a); }}
                          className="w-full text-left text-[10px] font-mono uppercase tracking-wide px-1.5 py-1 truncate border"
                          style={{ backgroundColor: `${colorFor(p.name)}33`, borderColor: `${colorFor(p.name)}AA`, color: "#fff" }}
                          title={`${a.employee_name} · ${a.start_time}-${a.end_time}`}
                        >
                          {a.start_time} {a.employee_name.split(" ")[0]}
                        </button>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Mobile: stacked project cards */}
      <div className="md:hidden space-y-2" data-testid="project-view-mobile">
        {projects.map((p) => {
          const projAssignments = days.flatMap((d) => {
            const k = fmt(d);
            return (byProj[p.id] || []).filter((a) => a.date === k).map((a) => ({ ...a, _day: d }));
          });
          return (
            <Card key={p.id} className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="h-3 w-3 inline-block shrink-0" style={{ background: colorFor(p.name) }} />
                <span className="font-medium truncate">{p.name}</span>
                <span className="ml-auto text-[10px] font-mono text-[#666] shrink-0">{projAssignments.length}</span>
              </div>
              {projAssignments.length === 0 && <div className="text-[10px] font-mono text-[#444]">— no assignments this week —</div>}
              <div className="space-y-1">
                {projAssignments.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => openDetails(a)}
                    className="w-full flex items-center gap-2"
                  >
                    <span className="text-[10px] font-mono uppercase text-[#888] w-10 shrink-0 text-left">{shortDay(a._day)}</span>
                    <span
                      className="flex-1 min-w-0 text-left text-[10px] font-mono uppercase tracking-wide px-1.5 py-1 truncate border"
                      style={{ backgroundColor: `${colorFor(p.name)}33`, borderColor: `${colorFor(p.name)}AA`, color: "#fff" }}
                    >
                      {a.start_time} · {a.employee_name}
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
}
